"use client";

import { useEffect } from "react";

const TIKTOK_PIXEL_ID = "CJRE13JC77U2Q32CC2R0";

declare global {
  interface Window {
    TiktokAnalyticsObject?: string;
    ttq?: any;
  }
}

// Module-level flag so we never insert the events.js <script> twice, even if
// `initTikTok()` is called rapidly before TikTok's own `ttq.loaded` flag is
// set (the other pixels — gtag, Meta — set a synchronous sentinel before
// the network request, but TikTok's stub doesn't).
let tiktokBootstrapped = false;

// Bootstraps the TikTok Pixel `ttq` queue and downloads the TikTok events SDK.
// The `<script>` tag is `async` so it never blocks parsing or paint; the
// `window.ttq` queue stub is set up synchronously inside this function so
// any caller that fires `ttq.track(...)` (e.g. `InitiateCheckout` from
// `PricingCard` or `Subscribe` on `/success`) hits a real queue instead
// of `undefined`.
function initTikTok() {
  if (typeof window === "undefined") return;
  if (tiktokBootstrapped) return;
  if (window.ttq && window.ttq.loaded) return;
  tiktokBootstrapped = true;

  const w = window as any;
  const t = "ttq";
  w.TiktokAnalyticsObject = t;
  const ttq = (w[t] = w[t] || []);
  ttq.methods = [
    "page",
    "track",
    "identify",
    "instances",
    "debug",
    "on",
    "off",
    "once",
    "ready",
    "alias",
    "group",
    "enableCookie",
    "disableCookie",
  ];
  ttq.setAndDefer = function (target: any, method: string) {
    target[method] = function () {
      target.push([method].concat(Array.prototype.slice.call(arguments, 0)));
    };
  };
  for (let i = 0; i < ttq.methods.length; i++) {
    ttq.setAndDefer(ttq, ttq.methods[i]);
  }
  ttq.instance = function (id: string) {
    const e = ttq._i[id] || [];
    for (let n = 0; n < ttq.methods.length; n++) {
      ttq.setAndDefer(e, ttq.methods[n]);
    }
    return e;
  };
  ttq.load = function (e: string, n?: any) {
    const i = "https://analytics.tiktok.com/i18n/pixel/events.js";
    ttq._i = ttq._i || {};
    ttq._i[e] = [];
    ttq._i[e]._u = i;
    ttq._t = ttq._t || {};
    ttq._t[e] = +new Date();
    ttq._o = ttq._o || {};
    ttq._o[e] = n || {};
    const o = document.createElement("script");
    o.type = "text/javascript";
    o.async = true;
    o.src = i + "?sdkid=" + e + "&lib=" + t;
    const a = document.getElementsByTagName("script")[0];
    if (a?.parentNode) {
      a.parentNode.insertBefore(o, a);
    } else {
      // Fallback: HTML may not have a <script> tag yet (e.g. body still
      // streaming). Append directly to <head> so we don't throw.
      document.head.appendChild(o);
    }
  };

  ttq.load(TIKTOK_PIXEL_ID);
  ttq.page();
}

// Loads on mount, deferred to browser idle time when supported. This pairs
// with the `async` `<script>` tag inside `initTikTok()` to keep the SDK off
// the LCP critical path while still guaranteeing it loads shortly after the
// page settles — never gated on user interaction, so server-redirect pages
// like `/success` (no clicks before tracking fires) still get the queue
// stubs in time.
function scheduleInit() {
  if (typeof window === "undefined") return;
  const ric = (window as Window & {
    requestIdleCallback?: (cb: () => void) => void;
  }).requestIdleCallback;
  if (ric) {
    ric(initTikTok);
  } else {
    setTimeout(initTikTok, 0);
  }
}

export default function TikTokPixel() {
  useEffect(() => {
    scheduleInit();
  }, []);

  return null;
}
